import { FunctionComponent, useState, useEffect } from 'react';
import styles from './DailySchedule.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import Schedule from './Schedule';
import clsx from 'clsx';
import { Moment } from 'moment-timezone';
import { uniq } from 'lodash';

interface DailyScheduleProps {
    from?: Moment | undefined;
    to?: Moment | undefined;
    schedule: Array<string>;
    onUpdate: (schedule: Array<string>) => void;
}

const DailySchedule: FunctionComponent<DailyScheduleProps> = ({ from, to, schedule, onUpdate }) => {
    const [doses, setDoses] = useState<string[]>(
        schedule || [from && to ? from.clone().add(15, 'minutes').format('h:mmA').toUpperCase() : '8:00 AM']
    );

    useEffect(() => {
        if (schedule && schedule.length > 0) {
            setDoses(schedule);
        }
    }, [schedule]);

    const handleAddDose = () => {
        let newTime: string;

        const scheduleMinutes = doses.map(time => {
            const [timePart, period] = time.split(/(am|pm)/i);
            const [hours, minutes] = timePart.split(':').map(Number);
            let hour24 = hours;
            if (period.toUpperCase() === 'PM' && hours !== 12) {
                hour24 += 12;
            } else if (period.toUpperCase() === 'AM' && hours === 12) {
                hour24 = 0;
            }
            return hour24 * 60 + minutes;
        });

        const maxMinutes = Math.max(...scheduleMinutes);
        const nextMinutes = maxMinutes + 15;

        const nextHour = Math.floor(nextMinutes / 60) % 24;
        const nextMinute = nextMinutes % 60;

        const nextDate = new Date();
        nextDate.setHours(nextHour, nextMinute, 0, 0);

        newTime = nextDate
            .toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
            .replace(' ', '')
            .toUpperCase();

        const newDoses = [...doses, newTime];
        setDoses(newDoses);
        updateMedicationSchedule(newDoses);
    };

    const handleRemoveDose = (doseIndex: number) => {
        const newDoses = doses.filter((_, i) => i !== doseIndex);
        setDoses(newDoses);
        updateMedicationSchedule(newDoses);
    };

    const handleDoseTimeChange = (doseIndex: number, time: string) => {
        const newDoses = [...doses];
        newDoses[doseIndex] = time;
        setDoses(newDoses);
        updateMedicationSchedule(newDoses);
    };

    const updateMedicationSchedule = (newDoses: string[]) => {
        onUpdate(newDoses);
    };

    return (
        <div className={styles.doseSchedule}>
            {uniq(doses).map((dose, doseIndex) => (
                <div
                    key={doseIndex}
                    className={styles.doseRow}
                >
                    <div className={styles.doseLabelContainer}>
                        <span className={styles.doseLabel}>Dose {doseIndex + 1}</span>
                        <span className={clsx(styles.doseLabel, styles.doseTime)}>{dose}</span>
                    </div>

                    <Schedule
                        id={doseIndex}
                        value={dose}
                        from={from}
                        to={to}
                        onChange={time => handleDoseTimeChange(doseIndex, time)}
                        placeholder="Select time"
                        usedTimes={doses}
                    />

                    {doseIndex > 0 && (
                        <FontAwesomeIcon
                            icon={faClose}
                            className={styles.removeDose}
                            onClick={() => handleRemoveDose(doseIndex)}
                        />
                    )}
                </div>
            ))}

            <button
                className={styles.addDoseButton}
                onClick={handleAddDose}
            >
                + Add Dose Schedule
            </button>
        </div>
    );
};

export default DailySchedule;
